// apps/academy/src/App.tsx
import { ComingSoonGate, useAuthStore } from '@neuroforge/shared/client'
import { LoadingSpinner } from '@neuroforge/ui'
import { lazy } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router'
import { RouterProvider } from 'react-router/dom'

import AcademyLayout from './components/AcademyLayout'
import VantaBackground from './components/VantaBackground'

const COMING_SOON = import.meta.env.VITE_COMING_SOON === 'true'

// Guard pour les routes authentifiées — redirige vers login avec return URL
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuthStore()
  const location = useLocation()
  if (isLoading)
    return (
      <div role="status" className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  if (!isAuthenticated) {
    return <Navigate to={`/auth/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
  }
  return <Outlet />
}

// Lazy loading des pages
const HomePage = lazy(() => import('./pages/HomePage'))
const CoursePage = lazy(() => import('./pages/CoursePage'))
const LessonPage = lazy(() => import('./pages/LessonPage'))
const ProgressPage = lazy(() => import('./pages/ProgressPage'))
const CatalogPage = lazy(() => import('./pages/CatalogPage')) // Academy K
// ProfilePage shared avec prop app
const ProfilePage = lazy(() =>
  import('@neuroforge/shared/client').then((m) => ({
    default: () => m.ProfilePage({ app: 'academy' }),
  })),
)
// Auth centralisée sur le Hub : les routes /auth/* locales redirigent vers lui.
// Seul /auth/callback reste local (handoff de token Hub → sous-app).
const HubAuthRedirect = lazy(() =>
  import('@neuroforge/shared/client').then((m) => ({ default: m.HubAuthRedirect })),
)
const AuthCallbackPage = lazy(() =>
  import('@neuroforge/shared/client').then((m) => ({ default: m.AuthCallbackPage })),
)
const NotFoundPage = lazy(() =>
  import('@neuroforge/shared/client').then((m) => ({
    default: () => m.NotFoundPage({ app: 'academy' }),
  })),
)
// Admin pages (Academy J)
const AdminLayout = lazy(() => import('./components/AdminLayout'))
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'))
const AdminCoursesPage = lazy(() => import('./pages/admin/AdminCoursesPage'))
const AdminCourseEditPage = lazy(() => import('./pages/admin/AdminCourseEditPage'))

const router = createBrowserRouter([
  {
    element: <AcademyLayout />,
    errorElement: <NotFoundPage />,
    children: [
      { path: '/', element: <CatalogPage /> },
      { path: '/catalog', element: <Navigate to="/" replace /> },
      { path: '/mon-parcours', element: <HomePage /> },
      { path: '/courses/:slug', element: <CoursePage /> },
      { path: '/courses/:courseSlug/lessons/:lessonSlug', element: <LessonPage /> },
      {
        element: <ProtectedRoute />,
        children: [
          { path: '/progress', element: <ProgressPage /> },
          { path: '/profile', element: <ProfilePage /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  // Admin routes (Academy J — layout admin dédié avec guard ADMIN)
  {
    path: '/admin',
    element: <AdminLayout />,
    errorElement: <NotFoundPage />,
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: 'courses', element: <AdminCoursesPage /> },
      { path: 'courses/:slug', element: <AdminCourseEditPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  // Auth centralisée Hub — /auth/callback reste local (handoff token),
  // tout autre /auth/* forward vers le Hub
  { path: '/auth/callback', element: <AuthCallbackPage />, errorElement: <NotFoundPage /> },
  { path: '/auth/*', element: <HubAuthRedirect />, errorElement: <NotFoundPage /> },
  { path: '*', element: <NotFoundPage /> },
])

export default function App() {
  return (
    <>
      <VantaBackground />
      <ComingSoonGate
        app="academy"
        enabled={COMING_SOON}
        hubUrl={import.meta.env.VITE_HUB_URL || '/'}
      >
        <RouterProvider router={router} />
      </ComingSoonGate>
    </>
  )
}
